import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { UsersService } from '../../shared/services/admin/users.service';
import { ApiService } from '../../shared/services/api/api.service';
import * as UsersActions from './users.actions';
import { selectUsersLastFilter } from './users.selectors';

@Injectable()
export class UsersEffects {
  private actions$ = inject(Actions);
  private usersService = inject(UsersService);
  private apiService = inject(ApiService);
  private store = inject(Store);

  loadUsers$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(UsersActions.loadUsers),
        withLatestFrom(this.store.select(selectUsersLastFilter)),
        switchMap(([action, lastFilter]) => {
          // Check if we have cached data with same filter
          const params: any = {
            page: action.page || lastFilter?.page || 1,
            page_size: action.pageSize || lastFilter?.pageSize || 20,
          };
          if (action.isActive !== undefined) {
            params.is_active = action.isActive;
          } else if (lastFilter?.isActive !== undefined) {
            params.is_active = lastFilter.isActive;
          }

          return this.usersService.getUsers(params, false).pipe(
            map((response) => UsersActions.loadUsersSuccess({ response })),
            catchError((error) => {
              this.apiService.showError(error.message || 'Failed to load users', 'Error');
              return of(UsersActions.loadUsersFailure({ error }));
            }),
          );
        }),
      );
    },
    { functional: false },
  );

  loadUserById$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(UsersActions.loadUserById),
        switchMap(({ userId }) =>
          this.usersService.getUserById(userId, false).pipe(
            map((user) => UsersActions.loadUserByIdSuccess({ user })),
            catchError((error) => {
              this.apiService.showError(error.message || 'Failed to load user', 'Error');
              return of(UsersActions.loadUserByIdFailure({ error }));
            }),
          ),
        ),
      );
    },
    { functional: false },
  );

  updateUserStatus$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(UsersActions.updateUserStatus),
        switchMap(({ userId, isActive }) =>
          this.usersService.updateUserStatus(userId, isActive).pipe(
            map((user) => {
              this.apiService.showSuccess(
                `User ${isActive ? 'activated' : 'deactivated'} successfully`,
                'Success',
              );
              return UsersActions.updateUserStatusSuccess({ user });
            }),
            catchError((error) => {
              this.apiService.showError(error.message || 'Failed to update user status', 'Error');
              return of(UsersActions.updateUserStatusFailure({ error }));
            }),
          ),
        ),
      );
    },
    { functional: false },
  );
}
